import { useQueries } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { ButtonPropsWithLabel } from 'src/components/Dialog';
import { Stack } from 'src/components/Stack';
import {
  fetchInvoices,
  isPayableOpenInvoice,
  pastDueInvoicesQueryKey,
} from 'src/models/payment/payment';

import { CheckinPastDuePaymentDialog } from './CheckinPastDuePaymentDialog';
import { UserContent } from './UserContent';
import { CheckinBlockerContentProps, CheckinBlockerUser } from './_types';

export type { CheckinBlockerContentProps } from './_types';

function hasUnresolvedBillingBlocker(user: CheckinBlockerUser) {
  return user.blockers.some(
    (blocker) => blocker.type === 'billing_blocked' && !blocker.resolved
  );
}

/**
 * Shared blocking-reason body used by both the CheckinBlockerDialog (React)
 * and future fullscreen/iframe pages (embedded in Ember).
 *
 * This component is intentionally presentation-only — keep host-specific
 * chrome (dialog padding, page layout, scroll wrappers) in the consumer.
 */
export function CheckinBlockerContent({
  users,
  showUserDetails = false,
  onOverride,
  onBillingResolved,
}: CheckinBlockerContentProps) {
  const { t } = useTranslation('translation', { keyPrefix: 'checkinBlocker' });
  const [paymentDialogUserId, setPaymentDialogUserId] = useState<string>();

  const billingBlockedUserIds = [
    ...new Set(
      users.filter(hasUnresolvedBillingBlocker).map((u) => String(u.id))
    ),
  ];

  const pastDueInvoiceQueries = useQueries({
    queries: billingBlockedUserIds.map((userId) => ({
      queryKey: pastDueInvoicesQueryKey(userId),
      queryFn: () =>
        fetchInvoices({
          userId,
          per: 100,
          filters: { pastDue: true },
        }),
      enabled: billingBlockedUserIds.length > 0,
    })),
  });

  const userIdHasPayablePastDue = (userId: string) => {
    const id = String(userId);
    const idx = billingBlockedUserIds.indexOf(id);
    if (idx < 0) return false;
    const q = pastDueInvoiceQueries[idx];
    if (!q?.data || q.isError) return false;
    return q.data.invoices.some(isPayableOpenInvoice);
  };

  const getPastDueInvoiceQuery = (userId: string) => {
    const idx = billingBlockedUserIds.indexOf(String(userId));
    if (idx < 0) return undefined;
    return pastDueInvoiceQueries[idx];
  };

  const isPastDueInvoiceInfoLoading = (userId: string) => {
    const q = getPastDueInvoiceQuery(userId);
    if (!q || q.isError) return false;
    return q.isPending || (q.isFetching && q.data === undefined);
  };

  const shouldShowPayBalanceAction = (userId: string) => {
    const q = getPastDueInvoiceQuery(userId);
    if (!q || q.isError) return false;
    if (isPastDueInvoiceInfoLoading(userId)) return true;
    return userIdHasPayablePastDue(userId);
  };

  const hasMultipleUsers = users.length > 1;
  const firstUser = users[0];

  const buildPayBalanceAction = (
    user: CheckinBlockerUser,
    loading: boolean
  ): ButtonPropsWithLabel => ({
    label: t('actions.updatePaymentMethod'),
    variant: 'bordered',
    loading,
    disabled: loading,
    onClick: () => setPaymentDialogUserId(user.id),
  });

  const buildOverrideAction = (
    user: CheckinBlockerUser,
    blocker: CheckinBlockerUser['blockers'][number]
  ): ButtonPropsWithLabel => ({
    label: t('actions.override'),
    variant: 'borderless',
    color: 'neutral',
    onClick: () =>
      void onOverride?.({
        scope: 'user',
        user,
        blocker,
      }),
  });

  const globalActions: ButtonPropsWithLabel[] | undefined = (() => {
    if (!firstUser) return undefined;

    if (hasMultipleUsers) {
      return onOverride
        ? [
            {
              label: t('actions.overrideAndCheckInAll'),
              variant: 'bordered',
              color: 'neutral',
              onClick: () => void onOverride({ scope: 'all' }),
            },
          ]
        : undefined;
    }

    const actions: ButtonPropsWithLabel[] = [];

    if (
      hasUnresolvedBillingBlocker(firstUser) &&
      shouldShowPayBalanceAction(String(firstUser.id))
    ) {
      actions.push(
        buildPayBalanceAction(
          firstUser,
          isPastDueInvoiceInfoLoading(String(firstUser.id))
        )
      );
    }

    if (onOverride) {
      actions.push({
        label: hasUnresolvedBillingBlocker(firstUser)
          ? t('actions.override')
          : t('actions.overrideAndCheckIn'),
        variant: 'borderless',
        color: 'neutral',
        onClick: () => void onOverride({ scope: 'user', user: firstUser }),
      });
    }

    return actions.length > 0 ? actions : undefined;
  })();

  const getBlockerActions = (
    user: CheckinBlockerUser,
    blocker: CheckinBlockerUser['blockers'][number]
  ): ButtonPropsWithLabel[] | undefined => {
    if (blocker.resolved) return undefined;

    const actions: ButtonPropsWithLabel[] = [];

    if (blocker.type === 'waiver_required' && blocker.documentUrl) {
      actions.push({
        label: t('actions.viewDocument'),
        variant: 'bordered',
        onClick: () => {
          window.open(blocker.documentUrl, '_blank', 'noopener,noreferrer');
        },
      });
    }

    if (hasMultipleUsers) {
      if (
        blocker.type === 'billing_blocked' &&
        shouldShowPayBalanceAction(String(user.id))
      ) {
        actions.push(
          buildPayBalanceAction(
            user,
            isPastDueInvoiceInfoLoading(String(user.id))
          )
        );
      }
      if (onOverride) {
        actions.push(buildOverrideAction(user, blocker));
      }
    }

    return actions.length > 0 ? actions : undefined;
  };

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        width: '100%',
      }}
    >
      <Stack alignItems="center" spacing={3} sx={{ pb: 2, pt: 1, px: 3 }}>
        <UserContent
          users={users}
          actions={globalActions}
          getBlockerActions={getBlockerActions}
          showUserDetails={showUserDetails}
        />
      </Stack>
      <CheckinPastDuePaymentDialog
        open={!!paymentDialogUserId}
        onClose={() => setPaymentDialogUserId(undefined)}
        onPaymentResolved={() => {
          if (paymentDialogUserId) {
            onBillingResolved?.(paymentDialogUserId);
          }
        }}
        userId={paymentDialogUserId ?? ''}
      />
    </Box>
  );
}
